/**
 * PrepareChatVideoThumbnailUseCase
 *
 * Client-side preparation of the chat VIDEO thumbnail for confidential upload.
 * The full video is handled by the server transcode + segment-encryption
 * pipeline (Tasks 2–7); ONLY the thumbnail is produced + encrypted client-side
 * here (the server used to ffmpeg a single frame — that pass moves to the
 * client now that chat media is E2EE).
 *
 * Orchestrates, in order:
 *   1. Capture a thumbnail frame from the source video
 *      (`VideoThumbnailService` — offscreen `<video>` + canvas, resized to
 *      the ≈480 longest-side target shared with the chat-image thumbnail).
 *   2. If a frame was produced, encrypt it with the chat ECDH→AES-GCM binary
 *      path (`EncryptBinaryUseCase`, Task 9 — random per-file IV prepended),
 *      under the SAME peer key `InitializeTextEncryptionUseCase.execute` derived
 *      for this conversation, and pair the ciphertext with its deterministic
 *      upload path (`{fileDir}thumbnail/{fileName}` — the SAME path the reworked
 *      `transcodeChatVideo` CF writes to `thumbnail`, Task 5).
 *   3. If NO frame was produced (the browser cannot decode the codec —
 *      consideration D), return `{ encryptedThumbnail: null }` with no path so
 *      the send flow SKIPS the thumbnail upload entirely. The message has no
 *      `thumbnail` and the bubble renders the placeholder frame + play icon
 *      (Task 13). A missing thumbnail must NOT abort the send.
 *
 * Web-first design — no Flutter parity (chat media encryption is Web-only).
 *
 * PURE DOMAIN: this use case does NOT upload, write Firestore, or dispatch. It
 * returns the encrypted thumbnail ciphertext (or null) + its target path; the
 * presentation/data send flow (Task 13/15) performs the actual storage upload
 * to that path when present and then fires the existing `transcodeChatContent`
 * callable. The CF derives the same path server-side, so no path is carried in
 * the payload.
 *
 * Pre-condition: the chat E2EE key was derived for the intended peer via
 * `InitializeTextEncryptionUseCase.execute`. The single-key-slot serialization
 * caveat from `EncryptBinaryUseCase`'s JSDoc applies — the caller serializes
 * init→prepare per peer.
 */

import {
  VideoThumbnailService,
  videoThumbnailService as defaultVideoThumbnailService,
} from '@/domain/services/video/videoThumbnailService';
import {
  EncryptBinaryUseCase,
  encryptBinaryUseCase,
} from '@/domain/usecases/textEncryption/encryptBinaryUseCase';
import { MediaUtils } from '@/domain/utils/mediaUtils';

/**
 * Result of preparing the chat video thumbnail.
 *
 * The frame-produced case carries the ciphertext + its deterministic upload
 * path; the codec-failure case carries `encryptedThumbnail: null` (and no path)
 * so the send flow skips the upload and the message has no `thumbnail`.
 */
export interface PreparedChatVideoThumbnail {
  /**
   * Encrypted thumbnail ciphertext (`[ iv ‖ ciphertext ]`), or `null` when the
   * client could not produce a frame (consideration D — codec fallback).
   */
  encryptedThumbnail: ArrayBuffer | null;
  /**
   * RELATIVE bucket path the encrypted thumbnail MUST be uploaded to —
   * `{fileDir}thumbnail/{fileName}`. The CF writes this verbatim to `thumbnail`.
   * `null` when no thumbnail was produced (nothing to upload).
   */
  thumbnailUploadPath: string | null;
}

export class PrepareChatVideoThumbnailUseCase {
  private readonly videoThumbnailService: VideoThumbnailService;
  private readonly encryptBinaryUseCase: EncryptBinaryUseCase;

  constructor(
    videoThumbnailService: VideoThumbnailService = defaultVideoThumbnailService,
    binaryEncryptUseCase: EncryptBinaryUseCase = encryptBinaryUseCase,
  ) {
    this.videoThumbnailService = videoThumbnailService;
    this.encryptBinaryUseCase = binaryEncryptUseCase;
  }

  /**
   * Produce the encrypted video thumbnail + its upload path, or the null
   * fallback when the codec cannot be decoded.
   *
   * @param source - The user-selected chat video File/Blob.
   * @param baseUploadPath - The 4-segment `chat/{user1}/{user2}/{fileName}`
   *   path (the same value the send flow passes as the transcode payload's
   *   `filePath`). Used to derive the deterministic ciphertext path.
   * @returns `{ encryptedThumbnail, thumbnailUploadPath }` when a frame was
   *   captured; `{ encryptedThumbnail: null, thumbnailUploadPath: null }` on the
   *   codec fallback (NOT an error — the upload is skipped).
   * @throws only if the ENCRYPTION step fails after a frame WAS captured (a real
   *   crypto error, not a codec limit). A codec limit returns null, not a throw.
   */
  async execute(
    source: Blob,
    baseUploadPath: string,
  ): Promise<PreparedChatVideoThumbnail> {
    // (1) Capture the thumbnail frame. `null` ⇒ codec fallback (consideration D).
    const thumbnail = await this.videoThumbnailService.capture(source);
    if (thumbnail === null) {
      // No frame: skip encrypt + upload entirely. Do NOT call encryptBinary.
      return { encryptedThumbnail: null, thumbnailUploadPath: null };
    }

    // (2) Encrypt the captured frame with the chat E2EE binary path (Task 9).
    const thumbnailBytes = await thumbnail.arrayBuffer();
    const encryptedThumbnail = await this.encryptBinaryUseCase.execute(thumbnailBytes);

    // (3) Derive the deterministic path the CF (Task 5) writes to `thumbnail`.
    // The video thumbnail shares the chat-image thumbnail's `{fileDir}thumbnail/`
    // destination, so we reuse the same path derivation.
    const { thumbnailPath } =
      MediaUtils.getChatImageCiphertextUploadPaths(baseUploadPath);

    return { encryptedThumbnail, thumbnailUploadPath: thumbnailPath };
  }
}

/** Shared singleton — mirrors the other use-case singletons. */
export const prepareChatVideoThumbnailUseCase = new PrepareChatVideoThumbnailUseCase();
